// ===============================================
// Purchase Management Page (Integrated Version)
// หน้าจัดการใบสั่งซื้อ - รองรับ Backend API แบบสมบูรณ์
// ===============================================

import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  DatePicker,
  Select,
  Row,
  Col,
  Statistic,
  message,
  Input,
  Popconfirm,
  Tooltip,
  Modal,
  Form,
  Alert,
  Spin,
  Typography,
} from 'antd';
import {
  PlusOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  SearchOutlined,
  FilePdfOutlined,
  ShoppingCartOutlined,
  DollarOutlined,
  CreditCardOutlined,
  CheckCircleOutlined,
  InboxOutlined,
  SwapOutlined,
  ExclamationCircleOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

// Import services (ใช้ PurchaseService ที่เราสร้างขึ้น)
import {
  purchasesAPI,
  formatCurrency,
  formatDate,
  handleAPIError
} from '../../services/PurchaseService';
import purchasePrintService from '../../services/purchasePrintService';
import SignatureLayoutControls from '../../components/SignatureLayoutControls';
import useDocumentSignatureLayout from '../../hooks/useDocumentSignatureLayout';
import {
  getCompanySettings,
  updateCompanySettings,
  uploadCompanyLogo,
  deleteCompanyLogo,
  getLogoUrl
} from '../../services/settingsService';

const { RangePicker } = DatePicker;
const { Option } = Select;
const { Search } = Input;
const { Title, Text } = Typography;

const PurchaseManagement = () => {
  const navigate = useNavigate();

  // State สำหรับข้อมูลหลัก
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');

  //สำหรับ modal แสดง pdf
  const [pdfModalVisible, setPdfModalVisible] = useState(false);
  const [pdfDataUrl, setPdfDataUrl] = useState('');
  const [pdfPreviewPurchase, setPdfPreviewPurchase] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  // State สำหรับตัวกรอง
  const [filters, setFilters] = useState({
    status: '',
    supplier_id: '',
    date_range: null,
  });

  // State สำหรับ pagination
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });

  // State สำหรับสถิติ
  const [statistics, setStatistics] = useState({
    totalOrders: 0,
    totalAmount: 0,
    cashOrders: 0,
    creditOrders: 0,
    pendingAmount: 0,
  });

  // State สำหรับการเปลี่ยนสถานะ
  const [statusModalVisible, setStatusModalVisible] = useState(false);
  const [selectedPurchase, setSelectedPurchase] = useState(null);
  const [statusForm] = Form.useForm();
  const [statusLoading, setStatusLoading] = useState(false);

  const [company, setCompany] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);
  const {
    signatureLayout,
    setSignatureLayout,
    signatureLayoutSaving,
    saveSignatureLayout
  } = useDocumentSignatureLayout('purchase_order', company, setCompany);

  useEffect(() => {
    if (!pdfModalVisible || !pdfPreviewPurchase) return undefined;

    let active = true;
    setPdfLoading(true);
    setPdfDataUrl('');

    purchasePrintService.generatePurchaseOrderPDF(company || {}, pdfPreviewPurchase, signatureLayout)
      .then(dataUrl => {
        if (active) setPdfDataUrl(dataUrl);
      })
      .catch(error => {
        console.error('Purchase PDF preview error:', error);
        if (active) message.error('ไม่สามารถสร้างตัวอย่างใบสั่งซื้อได้: ' + error.message);
      })
      .finally(() => {
        if (active) setPdfLoading(false);
      });

    return () => { active = false; };
  }, [pdfModalVisible, pdfPreviewPurchase, company, signatureLayout]);

  const handleSaveSignatureLayout = async () => {
    try {
      await saveSignatureLayout();
      message.success('บันทึกตำแหน่งลายเซ็นสำหรับใบสั่งซื้อแล้ว');
    } catch (error) {
      message.error(error.response?.data?.message || 'ไม่สามารถบันทึกตำแหน่งลายเซ็นได้');
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await getCompanySettings();
      //console.log('company data:', response.data.data);
      if (response.data.success) {
        const data = response.data.data;
        setCompany(data);
        setLogoUrl(data.logo_path ? getLogoUrl(`${data.logo_path}`) : null);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      message.error('ไม่สามารถโหลดข้อมูลบริษัทได้');
    } finally {
      setLoading(false);
      //console.log('image url:', logoUrl);
    }
  };

  // ดึงข้อมูลใบสั่งซื้อจาก API
  const fetchPurchases = async (page = 1) => {
    setLoading(true);
    try {
      const params = {
        page,
        limit: pagination.pageSize,
        search: searchText,
        ...filters,
      };

      // จัดการ date range
      if (filters.date_range) {
        params.date_from = filters.date_range[0].format('YYYY-MM-DD');
        params.date_to = filters.date_range[1].format('YYYY-MM-DD');
      }

      const response = await purchasesAPI.getAll(params);

      if (response) {
        setPurchases(response.data);
        setPagination(prev => ({
          ...prev,
          current: page,
          total: response.pagination.total,
        }));
        calculateStatistics(response.data);
      } else {
        message.error(response.message || 'เกิดข้อผิดพลาดในการดึงข้อมูล');
      }
    } catch (error) {
      console.error('Fetch purchases error:', error);
      message.error(handleAPIError(error));
    } finally {
      setLoading(false);
    }
  };

  // คำนวณสถิติจากข้อมูลที่ได้
  const calculateStatistics = (data) => {
    const stats = {
      totalOrders: data.length,
      totalAmount: data.reduce((sum, p) => sum + parseFloat(p.total_amount || 0), 0),
      cashOrders: data.filter(p => p.payment_type === 'cash').length,
      creditOrders: data.filter(p => p.payment_type === 'credit').length,
      pendingAmount: data
        .filter(p => p.payment_type === 'credit')
        .reduce((sum, p) => sum + parseFloat(p.remaining_amount || 0), 0),
    };
    setStatistics(stats);
  };

  // เปลี่ยนตัวกรอง
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  // ค้นหา
  const handleSearch = () => {
    fetchPurchases(1);
  };

  // รีเซ็ตตัวกรอง
  const handleResetFilters = () => {
    setFilters({
      status: '',
      supplier_id: '',
      date_range: null,
    });
    setSearchText('');
    // จะ trigger useEffect ที่ watch filters
  };

  // สถานะของใบสั่งซื้อ
  const getStatusTag = (status) => {
    const statusConfig = {
      draft: { color: 'default', text: 'ร่าง' },
      pending: { color: 'processing', text: 'รอดำเนินการ' },
      confirmed: { color: 'blue', text: 'ยืนยันแล้ว' },
      approved: { color: 'purple', text: 'อนุมัติแล้ว' },
      partially_received: { color: 'orange', text: 'รับสินค้าบางส่วน' },
      completed: { color: 'green', text: 'เสร็จสิ้น' },
      cancelled: { color: 'red', text: 'ยกเลิก' },
    };

    const config = statusConfig[status] || { color: 'default', text: status };
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  // ประเภทการชำระเงิน
  const getPaymentTypeTag = (type) => {
    return type == 'cash' ? (
      <Tag color="green" icon={<DollarOutlined />}>เงินสด</Tag>
    ) : (
      <Tag color="orange" icon={<CreditCardOutlined />}>เงินเชื่อ</Tag>
    );
  };

  // ลบใบสั่งซื้อ
  const handleDelete = async (id) => {
    try {
      setLoading(true);
      const response = await purchasesAPI.delete(id);

      if (response.success) {
        message.success(response.message || 'ลบใบสั่งซื้อสำเร็จ');
        fetchPurchases(pagination.current);
      } else {
        message.error(response.message || 'เกิดข้อผิดพลาดในการลบ');
      }
    } catch (error) {
      message.error(handleAPIError(error));
    } finally {
      setLoading(false);
    }
  };

  // สร้างใบรับสินค้าเข้าคลัง
  const handleCreateGoodsReceipt = (purchase) => {
    navigate(`/purchases/goods-receipt/create/${purchase.id}`, {
      state: { purchase: purchase }
    });
  };

  // ตรวจสอบว่าสามารถสร้างใบรับสินค้าได้หรือไม่
  const canCreateGoodsReceipt = (purchase) => {
    return purchase.can_create_receipt || ['confirmed', 'approved', 'partially_received'].includes(purchase.status);
  };

  // เปิด Modal เปลี่ยนสถานะ
  const handleChangeStatus = (purchase) => {
    setSelectedPurchase(purchase);
    setStatusModalVisible(true);
    statusForm.resetFields();
  };

  // ดึงตัวเลือกสถานะที่สามารถเปลี่ยนได้
  const getAvailableStatuses = (currentStatus) => {
    const validTransitions = {
      'draft': [
        { value: 'confirmed', label: 'ยืนยันแล้ว', color: 'blue' },
        { value: 'approved', label: 'อนุมัติแล้ว', color: 'purple' },
        { value: 'cancelled', label: 'ยกเลิก', color: 'red' },
      ],
      'pending': [
        { value: 'confirmed', label: 'ยืนยันแล้ว', color: 'blue' },
        { value: 'approved', label: 'อนุมัติแล้ว', color: 'purple' },
        { value: 'cancelled', label: 'ยกเลิก', color: 'red' },
      ],
      'confirmed': [
        { value: 'partially_received', label: 'รับสินค้าบางส่วน', color: 'orange' },
        { value: 'completed', label: 'เสร็จสิ้น', color: 'green' },
        { value: 'cancelled', label: 'ยกเลิก', color: 'red' },
      ],
      'approved': [
        { value: 'partially_received', label: 'รับสินค้าบางส่วน', color: 'orange' },
        { value: 'completed', label: 'เสร็จสิ้น', color: 'green' },
        { value: 'cancelled', label: 'ยกเลิก', color: 'red' },
      ],
      'partially_received': [
        { value: 'completed', label: 'เสร็จสิ้น', color: 'green' },
        { value: 'cancelled', label: 'ยกเลิก', color: 'red' },
      ],
      'completed': [],
      'cancelled': [],
    };

    return validTransitions[currentStatus] || [];
  };

  // ยืนยันการเปลี่ยนสถานะ
  const handleStatusSubmit = async (values) => {
    setStatusLoading(true);
    try {
      const response = await purchasesAPI.updateStatus(
        selectedPurchase.id,
        values.status,
        values.notes
      );

      if (response.success) {
        message.success(response.message || 'เปลี่ยนสถานะสำเร็จ');
        setStatusModalVisible(false);
        setSelectedPurchase(null);
        statusForm.resetFields();
        fetchPurchases(pagination.current);
      } else {
        message.error(response.message || 'เกิดข้อผิดพลาดในการเปลี่ยนสถานะ');
      }
    } catch (error) {
      message.error(handleAPIError(error));
    } finally {
      setStatusLoading(false);
    }
  };

  // ปิด Modal เปลี่ยนสถานะ
  const handleStatusCancel = () => {
    setStatusModalVisible(false);
    setSelectedPurchase(null);
    statusForm.resetFields();
  };

  // ตรวจสอบว่าสามารถเปลี่ยนสถานะได้หรือไม่
  const canChangeStatus = (purchase) => {
    return purchase.can_change_status || !['completed', 'cancelled'].includes(purchase.status);
  };

  // พิมพ์ใบสั่งซื้อ
  const handlePrintPurchaseOrder = async (purchase) => {
    // TODO: เรียกใช้ฟังก์ชันพิมพ์ PDF
    message.info('พิมพ์ใบสั่งซื้อ');
    try {
      console.log('ใบสั่งซื้อ', purchase);

      // ดึงข้อมูลใบสั่งซื้อ
      const purchaseData = await purchasesAPI.getById(purchase.id);
      console.log('purchaseData:', purchaseData);
      if (purchaseData.success) {
        setPdfPreviewPurchase(purchaseData.data);
        setPdfModalVisible(true);
      }

    } catch (error) {
      message.error('เกิดข้อผิดพลาดในการสร้างเอกสาร');
      console.error('PDF generation error:', error);
    }

  };


  // เปลี่ยนหน้า
  const handleTableChange = (paginationData) => {
    fetchPurchases(paginationData.current);
  };

  // คอลัมน์ตาราง
  const columns = [
    {
      title: 'เลขที่ใบสั่งซื้อ',
      dataIndex: 'purchase_number',
      key: 'purchase_number',
      width: 150,
      fixed: 'left',
      sorter: (a, b) => a.purchase_number.localeCompare(b.purchase_number),
    },
    {
      title: 'วันที่สั่งซื้อ',
      dataIndex: 'purchase_date',
      key: 'purchase_date',
      width: 120,
      render: (date) => formatDate(date),
      sorter: (a, b) => new Date(a.purchase_date) - new Date(b.purchase_date),
    },
    {
      title: 'ผู้จำหน่าย',
      dataIndex: 'supplier_name',
      key: 'supplier_name',
      width: 200,
      sorter: (a, b) => a.supplier_name.localeCompare(b.supplier_name),
    },
    {
      title: 'ประเภท',
      dataIndex: 'payment_type',
      key: 'payment_type',
      width: 100,
      render: (type) => getPaymentTypeTag(type),
    },
    {
      title: 'จำนวนรายการ',
      dataIndex: 'item_count',
      key: 'item_count',
      width: 120,
      align: 'center',
      render: (count) => (
        <Tag color="blue">{count} รายการ</Tag>
      ),
    },
    {
      title: 'ยอดรวม',
      dataIndex: 'total_amount',
      key: 'total_amount',
      width: 130,
      align: 'right',
      render: (amount) => formatCurrency(amount),
      sorter: (a, b) => parseFloat(a.total_amount) - parseFloat(b.total_amount),
    },
    {
      title: 'ภาษีซื้อ',
      dataIndex: 'tax_amount',
      key: 'tax_amount',
      width: 130,
      align: 'right',
      render: (tax_amount, record) => (
        <span style={{ color: '#52c41a' }}>
          {formatCurrency(tax_amount)}
        </span>
      ),
    },
    {
      title: 'สถานะ',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => getStatusTag(status),
    },
    {
      title: 'การดำเนินการ',
      key: 'actions',
      width: 250,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Tooltip title="ดูรายละเอียด">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => navigate(`/purchases/view/${record.id}`)}
              size="small"
            />
          </Tooltip>

          {/* {(record.can_edit || record.status === 'draft') && ( */}
            <Tooltip title="แก้ไข">
              <Button
                type="text"
                icon={<EditOutlined />}
                onClick={() => navigate(`/purchases/edit/${record.id}`)}
                size="small"
              />
            </Tooltip>
          {/* )} */}

          {canChangeStatus(record) && (
            <Tooltip title="เปลี่ยนสถานะ">
              <Button
                type="text"
                icon={<SwapOutlined />}
                onClick={() => handleChangeStatus(record)}
                size="small"
                style={{ color: '#722ed1' }}
              />
            </Tooltip>
          )}

          {canCreateGoodsReceipt(record) && (
            <Tooltip title="สร้างใบรับสินค้าเข้าคลัง">
              <Button
                type="text"
                icon={<InboxOutlined />}
                onClick={() => handleCreateGoodsReceipt(record)}
                size="small"
                style={{ color: '#52c41a' }}
              />
            </Tooltip>
          )}

          <Tooltip title="พิมพ์ใบสั่งซื้อ">
            <Button
              type="text"
              icon={<FilePdfOutlined />}
              onClick={() => handlePrintPurchaseOrder(record)}
              size="small"
            />
          </Tooltip>

          {(record.can_delete || record.status === 'draft') && (
            <Popconfirm
              title="ยืนยันการลบ"
              description="คุณแน่ใจหรือไม่ที่จะลบใบสั่งซื้อนี้?"
              onConfirm={() => handleDelete(record.id)}
              okText="ลบ"
              cancelText="ยกเลิก"
              icon={<ExclamationCircleOutlined style={{ color: 'red' }} />}
            >
              <Tooltip title="ลบ">
                <Button
                  type="text"
                  icon={<DeleteOutlined />}
                  danger
                  size="small"
                />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  // UseEffect hooks
  useEffect(() => {
    fetchPurchases(1);
  }, [filters]); // เมื่อ filters เปลี่ยน

  useEffect(() => {
    if (searchText === '') {
      fetchPurchases(1); // Auto search when clear
    }
  }, [searchText]);

  return (
    <div style={{ padding: '24px' }}>

      <Row justify="space-between" align="bottom" gutter={[16, 16]} style={{ marginBottom: 20 }}>
        <Col>
          <Title level={2} style={{ marginBottom: 2 }}><ShoppingCartOutlined /> ระบบจัดซื้อ</Title>
          <Text type="secondary">จัดการใบสั่งซื้อและรับสินค้าเข้าคลังจากเอกสาร PO</Text>
        </Col>
        <Col>
          <Space wrap>
            <Button icon={<InboxOutlined />} onClick={() => navigate('/purchases/goods-receipts')}>
              ใบรับสินค้าเข้าคลัง
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/purchases/create')}>
              สร้างใบสั่งซื้อ
            </Button>
          </Space>
        </Col>
      </Row>

      {/* Modal แสดง PDF */}
      <Modal
        title="ใบสั่งซื้อสินค้า"
        open={pdfModalVisible}
        onCancel={() => setPdfModalVisible(false)}
        footer={null}
        width={'100%'}
        bodyStyle={{ height: '80vh', display: 'flex', flexDirection: 'column' }}
      >
        <SignatureLayoutControls
          documentType="purchase_order"
          value={signatureLayout}
          onChange={setSignatureLayout}
          onSave={handleSaveSignatureLayout}
          saving={signatureLayoutSaving}
        />
        {pdfLoading ? <Spin size="large" style={{ margin: 'auto' }} /> : pdfDataUrl && (
          <iframe
            src={pdfDataUrl}
            style={{ width: '100%', flex: 1, minHeight: 0 }}
            frameBorder="0"
            title="ตัวอย่างเอกสาร"
          />
        )}
      </Modal>

      {/* สถิติรวม */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="ใบสั่งซื้อทั้งหมด"
              value={statistics.totalOrders}
              prefix={<ShoppingCartOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="ยอดซื้อรวม"
              value={statistics.totalAmount}
              prefix={<DollarOutlined />}
              formatter={(value) => formatCurrency(value)}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="การซื้อเงินสด"
              value={statistics.cashOrders}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="ยอดเจ้าหนี้คงค้าง"
              value={statistics.pendingAmount}
              prefix={<CreditCardOutlined />}
              formatter={(value) => formatCurrency(value)}
              valueStyle={{ color: '#fa8c16' }}
            />
          </Card>
        </Col>
      </Row>

      {/* ตัวกรองและการค้นหา */}
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={16} align="middle">
          <Col xs={24} sm={12} md={6}>
            <Search
              placeholder="ค้นหาเลขที่ใบสั่งซื้อ หรือ ผู้จำหน่าย..."
              allowClear
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onSearch={handleSearch}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Select
              placeholder="สถานะ"
              allowClear
              value={filters.status}
              onChange={(value) => handleFilterChange('status', value)}
              style={{ width: '100%' }}
            >
              <Option value="draft">ร่าง</Option>
              <Option value="pending">รอดำเนินการ</Option>
              <Option value="confirmed">ยืนยันแล้ว</Option>
              <Option value="approved">อนุมัติแล้ว</Option>
              <Option value="partially_received">รับสินค้าบางส่วน</Option>
              <Option value="completed">เสร็จสิ้น</Option>
              <Option value="cancelled">ยกเลิก</Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <RangePicker
              placeholder={['วันที่เริ่มต้น', 'วันที่สิ้นสุด']}
              value={filters.date_range}
              onChange={(dates) => handleFilterChange('date_range', dates)}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Space>
              <Button
                type="primary"
                icon={<SearchOutlined />}
                onClick={handleSearch}
                loading={loading}
              >
                ค้นหา
              </Button>
              <Button
                onClick={handleResetFilters}
                disabled={loading}
              >
                รีเซ็ต
              </Button>
              <Button
                icon={<ReloadOutlined />}
                onClick={() => fetchPurchases(pagination.current)}
                loading={loading}
              >
                รีเฟรช
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* ตารางข้อมูล */}
      <Card
        title={
          <Space>
            <ShoppingCartOutlined />
            รายการใบสั่งซื้อ
          </Space>
        }
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate('/purchases/create')}
            size="large"
          >
            สร้างใบสั่งซื้อใหม่
          </Button>
        }
      >
        <Table
          columns={columns}
          dataSource={purchases}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} จาก ${total} รายการ`,
          }}
          onChange={handleTableChange}
          scroll={{ x: 1500 }}
          size="small"
        />
      </Card>

      {/* Modal เปลี่ยนสถานะ */}
      <Modal
        title={
          <Space>
            <SwapOutlined />
            เปลี่ยนสถานะใบสั่งซื้อ
          </Space>
        }
        open={statusModalVisible}
        onCancel={handleStatusCancel}
        footer={null}
        width={500}
      >
        {selectedPurchase && (
          <Spin spinning={statusLoading}>
            <Form
              form={statusForm}
              layout="vertical"
              onFinish={handleStatusSubmit}
            >
              <div style={{
                background: '#f0f0f0',
                padding: 16,
                borderRadius: 8,
                marginBottom: 16
              }}>
                <Row gutter={16}>
                  <Col span={12}>
                    <strong>เลขที่ใบสั่งซื้อ:</strong><br />
                    {selectedPurchase.purchase_number}
                  </Col>
                  <Col span={12}>
                    <strong>สถานะปัจจุบัน:</strong><br />
                    {getStatusTag(selectedPurchase.status)}
                  </Col>
                </Row>
                <Row style={{ marginTop: 8 }}>
                  <Col span={24}>
                    <strong>ผู้จำหน่าย:</strong><br />
                    {selectedPurchase.supplier_name}
                  </Col>
                </Row>
              </div>

              {getAvailableStatuses(selectedPurchase.status).length === 0 ? (
                <Alert
                  message="ไม่สามารถเปลี่ยนสถานะได้"
                  description={`ใบสั่งซื้อที่มีสถานะ "${selectedPurchase.status}" ไม่สามารถเปลี่ยนสถานะได้อีก`}
                  type="info"
                  icon={<WarningOutlined />}
                  style={{ marginBottom: 16 }}
                />
              ) : (
                <>
                  <Form.Item
                    name="status"
                    label="เปลี่ยนเป็นสถานะ"
                    rules={[
                      { required: true, message: 'กรุณาเลือกสถานะใหม่' }
                    ]}
                  >
                    <Select
                      placeholder="เลือกสถานะใหม่"
                      size="large"
                    >
                      {getAvailableStatuses(selectedPurchase.status).map(status => (
                        <Select.Option key={status.value} value={status.value}>
                          <Tag color={status.color}>{status.label}</Tag>
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>

                  <Form.Item
                    name="notes"
                    label="หมายเหตุ (ไม่บังคับ)"
                  >
                    <Input.TextArea
                      rows={3}
                      placeholder="ระบุเหตุผลหรือหมายเหตุการเปลี่ยนสถานะ..."
                    />
                  </Form.Item>

                  <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                    <Space>
                      <Button onClick={handleStatusCancel} disabled={statusLoading}>
                        ยกเลิก
                      </Button>
                      <Button
                        type="primary"
                        htmlType="submit"
                        icon={<CheckCircleOutlined />}
                        loading={statusLoading}
                      >
                        ยืนยันการเปลี่ยนสถานะ
                      </Button>
                    </Space>
                  </Form.Item>
                </>
              )}
            </Form>
          </Spin>
        )}
      </Modal>
    </div>
  );
};

export default PurchaseManagement;
